/**
 * Loads a script.
 * @param {string} src - Url of script to load.
 * @returns {Promise}
 */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

/* global DetailsDisclosure, trapFocus, removeTrapFocus */

if (!customElements.get('cart-drawer-disclosure')) {
  class CartDrawerDisclosure extends DetailsDisclosure {
    constructor() {
      super();
      this.openBtn = this.querySelector('summary');
      this.closeBtn = this.querySelector('.js-close');
      this.overlay = document.getElementById('cart-drawer').querySelector('.overlay');
    }

    /**
     * Handles 'click' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.matches('.js-close')) return;
      this.close();
    }

    /**
     * Opens the details element.
     */
    open() {
      this.overlay.classList.add('is-visible');
      super.open();
      trapFocus(this);

      // Create event handler variables (so the bound event listeners can be removed).
      this.clickHandler = this.clickHandler || this.handleClick.bind(this);
      this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();

      // Add event listeners (for while disclosure is open).
      this.addEventListener('click', this.clickHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.overlay.addEventListener('click', this.clickHandler);
    }

    /**
     * Closes the details element.
     */
    close() {
      this.overlay.classList.remove('is-visible');
      super.close();
      removeTrapFocus(this.openBtn);

      this.removeEventListener('click', this.clickHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      this.overlay.removeEventListener('click', this.clickHandler);
    }
  }

  customElements.define('cart-drawer-disclosure', CartDrawerDisclosure);
}

/* global DockableSideDrawer */

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends DockableSideDrawer {
    constructor() {
      super();

      this.cartSummary = this.querySelector('.cart-drawer__summary');
      this.init();
      this.bindEvents();
    }

    disconnectedCallback() {
      document.removeEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.removeEventListener('on:bfcache:load-restore', this.refreshHandler);
      document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:select', this.sectionSelectHandler);
        document.removeEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      if (this.dcbLoadedHandler) {
        document.removeEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }
    }

    init() {
      const cartIcon = document.getElementById('cart-icon');
      if (cartIcon) {
        cartIcon.setAttribute('role', 'button');
        cartIcon.setAttribute('aria-haspopup', 'dialog');

        cartIcon.addEventListener('click', (evt) => {
          if (!this.isDocked) {
            evt.preventDefault();
            this.open(cartIcon);
          }
        });

        cartIcon.addEventListener('keydown', (evt) => {
          if (!this.isDocked) {
            if (evt.key !== ' ') return;
            evt.preventDefault();
            this.open(cartIcon);
          }
        });
      }
    }

    bindEvents() {
      if (Shopify.designMode) {
        this.sectionSelectHandler = this.handleSectionSelect.bind(this);
        this.sectionDeselectHandler = this.handleSectionDeselect.bind(this);
        document.addEventListener('shopify:section:select', this.sectionSelectHandler);
        document.addEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
      this.closeDrawerViaEventHandler = this.close.bind(this, null);
      this.addEventListener('click', this.handleEmptyCart.bind(this));

      document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (this.cartSummary && this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
        this.dcbLoadedHandler = this.dcbLoadedHandler || CartDrawer.recalculateCssVarHeights;
        document.addEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }

      this.refreshHandler = this.refreshCartDrawer.bind(this);
      document.addEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.addEventListener('on:bfcache:load-restore', this.refreshHandler);
    }

    /**
     * Clears the cart
     * @param {object} evt - Event object.
     */
    handleEmptyCart(evt) {
      if (evt.target.matches('.js-empty-cart')) {
        evt.preventDefault();

        // eslint-disable-next-line no-alert, no-restricted-globals
        if (confirm(theme.strings.confirmEmptyCart)) {
          this.querySelectorAll('.cart-item').forEach((cartItem, index) => {
            setTimeout(() => cartItem.classList.add('cart-item--out'), index * 50);
          });

          try {
            fetch(`${theme.routes.cartClear}.js`, {
              method: 'POST'
            }).then((response) => {
              if (!response.ok) throw new Error(response.status);

              // Broadcast individual events to update product cards
              this.querySelectorAll('.cart-item').forEach((cartItem) => {
                this.dispatchEvent(new CustomEvent('on:line-item:change', {
                  bubbles: true,
                  detail: {
                    variantId: cartItem.dataset.variantId,
                    newQuantity: 0
                  }
                }));
              });

              this.refreshCartDrawer();
            });
          } catch (error) {
            window.location.href = evt.target.href;
          }
        }
      }
    }

    /**
     * Handle when the section is selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionSelect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.open();
    }

    /**
     * Handle when the section is de-selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionDeselect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.close();
    }

    /**
     * Handle when the drawer is opened via an event
     * @param {object} evt - Event object.
     */
    handleDrawerOpenViaEvent(evt) {
      this.open(evt.detail ? evt.detail.opener : null);
    }

    /**
     * Opens the drawer.
     * @param {Element} [opener] - Element that triggered opening of the drawer.
     * @param {Element} [elementToFocus] - Element to focus after drawer opened.
     */
    open(opener, elementToFocus) {
      // Get the quick add drawer web component, if it's currently open and close it
      const quickAddDrawer = document.querySelector('quick-add-drawer[aria-hidden="false"]');
      const overlay = document.querySelector('.js-overlay.is-visible');

      if (quickAddDrawer) {
        if (overlay) overlay.style.transitionDelay = '200ms';
        quickAddDrawer.close();
      }

      // If the cart drawer is open, wait a few ms for a more optimal ux/animation
      setTimeout(() => {
        super.open(opener, elementToFocus, () => {
          if (this.cartSummary && this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
            CartDrawer.recalculateCssVarHeights();
          }
        });

        if (this.cartSummary && this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
          CartDrawer.recalculateCssVarHeights();
        }

        if (overlay) overlay.style.transitionDelay = '';
      }, quickAddDrawer ? 200 : 0);
      window.initLazyImages();
    }

    /**
     * Attempts to refresh any cart items (if present). Failing that, it refreshes the entire cart
     * drawer
     * @param {boolean} [dontRefreshCartItems=false] - Prevents the refresh of cart items, and does
     * a straight refresh of the whole cart
     */
    async refreshCartDrawer(dontRefreshCartItems) {
      const drawerDbg = (step, data) => {
        if (window.CCFlareDebug && window.CCFlareDebug.enabled()) {
          window.CCFlareDebug.log('drawer', 'refreshCartDrawer', step, data);
        }
      };
      const drawerTime = (id, step, data) => {
        if (window.CCFlareDebug && window.CCFlareDebug.enabled()) {
          window.CCFlareDebug.timeStart('drawer:' + id, 'drawer', step, data);
        }
      };
      const drawerTimeEnd = (id, step, data) => {
        if (window.CCFlareDebug && window.CCFlareDebug.enabled()) {
          window.CCFlareDebug.timeEnd('drawer:' + id, 'drawer', step, data);
        }
      };
      const _rcdLog = () => {};
      _rcdLog('refreshCartDrawer START');
      drawerTime('refresh', 'refreshCartDrawer', { dontRefreshCartItems: !!dontRefreshCartItems });
      try {
        const cartItems = this.querySelector('cart-items[data-empty="false"]');
        if (cartItems && !dontRefreshCartItems) {
          _rcdLog('path → delegating to cartItems.refreshCartItems');
          drawerDbg('path', { via: 'cartItems.refreshCartItems' });
          await cartItems.refreshCartItems();
          _rcdLog('cartItems.refreshCartItems returned');
        } else {
          _rcdLog('path → sections fetch (no cart-items or dontRefreshCartItems=true)');
          drawerDbg('path', { via: 'sections fetch' });
          const sectionIds = this.getSectionsToRender().map((section) => section.section);
          // FIX: fire sections + /cart.js in parallel — independent requests.
          // Wrap cartJson in a never-rejecting promise so a failure doesn't swallow sections.
          // Also guard CartItems with typeof — cart-items.js may not be loaded yet on first ATC.
          _rcdLog('sections + cart.js fetch START (parallel)');
          const sectionsPromise = fetch(`?sections=${sectionIds.join(',')}`);
          const cartJsonPromise2 = Promise.resolve().then(() => {
            const _CI = typeof CartItems !== 'undefined' ? CartItems : null;
            return (_CI && _CI.fetchCartJson)
              ? _CI.fetchCartJson()
              : fetch(`${theme.routes.cart}.js`).then(r => r.json());
          }).catch(() => null);

          const cartResponse = await sectionsPromise;
          _rcdLog('sections fetch DONE (status ' + cartResponse.status + ')');

          // Parse both in parallel; cartJsonPromise2 never rejects (returns null on failure)
          const [sections, cartJsData] = await Promise.all([cartResponse.json(), cartJsonPromise2]);
          _rcdLog('cart.js fetch DONE');

          // Always render sections — this must happen even if cart.js failed
          this.renderContents({ sections }, false);
          CartDrawer.recalculateCssVarHeights();

          let cartDataForEvent = null;
          try {
            if (cartJsData) {
              cartDataForEvent = cartJsData;
              if (window.ccPublishCartSnapshot) {
                window.ccPublishCartSnapshot(cartDataForEvent, 'cart-drawer-refresh');
              }
              const cartItemsEl = this.querySelector('cart-items');
              if (cartItemsEl && cartItemsEl.checkAndHandleFreeProductImmediately) {
                // FIX: skip redundant check if one ran in the last 5s (already ran pre-refresh)
                const _msSinceFreebie = Date.now() - (window.__lastFreebieCheckMs || 0);
                if (_msSinceFreebie < 5000) {
                  _rcdLog(`free product check SKIPPED — last ran ${_msSinceFreebie}ms ago`);
                } else {
                  _rcdLog('free product check (2nd pass) START');
                  cartDataForEvent = await cartItemsEl.checkAndHandleFreeProductImmediately(cartDataForEvent) || cartDataForEvent;
                  _rcdLog('free product check (2nd pass) DONE');
                }
              }
            }
          } catch (error) {
          }

          if (typeof window.ccDispatchCartChange === 'function') {
            window.ccDispatchCartChange({
              cart: cartDataForEvent,
              cartUpdated: true,
              source: 'cart-drawer-refresh'
            });
          } else {
            document.dispatchEvent(new CustomEvent('on:cart:change', {
              bubbles: true,
              detail: { cartUpdated: true, cart: cartDataForEvent }
            }));
          }

        }
      } catch (error) {
        drawerDbg('error', { error: String(error) });
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg ? this.errorMsg.textContent : null
          }
        }));
      } finally {
        drawerTimeEnd('refresh', 'refreshCartDrawer');
        _rcdLog('refreshCartDrawer DONE');
      }
    }

    /**
     * Renders the contents of the specified sections to update.
     * @param {object} data - Cart data object.
     * @param {boolean} [openDrawer=true] - Open the cart drawer after rendering sections.
     */
    async renderContents(data, openDrawer = true) {
      const _rcLog = () => {};

      _rcLog('START', { openDrawer, hasSections: !!(data && data.sections), scriptsLoaded: !!this.scriptsLoaded });

      if (!this.scriptsLoaded) {
        _rcLog('script-load: checking cart-items script');
        const needsCartItems = !document.querySelector(`script[src="${theme.scripts.cartItems}"]`);
        if (needsCartItems) {
          _rcLog('script-load: loading cart-items.js START');
          await loadScript(theme.scripts.cartItems);
          _rcLog('script-load: loading cart-items.js DONE');
        } else {
          _rcLog('script-load: cart-items.js already present');
        }

        this.scriptsLoaded = true;
      }

      const sections = this.getSectionsToRender();
      _rcLog('DOM-update: rendering ' + sections.length + ' sections', sections.map(s => s.id));

      sections.forEach((section) => {
        const el = document.getElementById(section.id);
        if (el) {
          const t0 = performance.now();
          const html = data.sections[section.section];
          if (!html) {
            _rcLog('DOM-update: MISSING html for section ' + section.section + ' (id=' + section.id + ')');
          }
          el.innerHTML = CartDrawer.getElementHTML(html, section.selector);
          _rcLog(`DOM-update: #${section.id} innerHTML set (${(performance.now() - t0).toFixed(0)}ms)`);

          // Update the cart drawer quantity
          if (section.id === 'cart-icon-bubble') {
            const drawerCount = this.querySelector('.cart-drawer__count');
            if (drawerCount) {
              // Update directly from data.item_count instead of extracting from HTML
              if (data.item_count > 0) {
                const itemText = data.item_count === 1 ? 'Item' : 'Items';
                drawerCount.textContent = `(${data.item_count} ${itemText})`;
                drawerCount.hidden = false;
                drawerCount.style.display = '';
              } else {
                drawerCount.textContent = '';
                drawerCount.hidden = true;
                drawerCount.style.display = 'none';
              }
            } else {
              // Create element if it doesn't exist
              const drawerHeader = this.querySelector('.drawer__header');
              if (drawerHeader) {
                const headerTitleContainer = drawerHeader.querySelector('.flex.items-center');
                if (headerTitleContainer) {
                  // If data.item_count is not available, fetch from cart.js
                  let itemCount = data.item_count;
                  if (itemCount === undefined) {
                    fetch(`${theme.routes.cart}.js`)
                      .then(response => response.json())
                      .then(cartData => {
                        itemCount = cartData.item_count;
                        if (itemCount > 0) {
                          const newDrawerCount = document.createElement('span');
                          newDrawerCount.className = 'cart-drawer__count text-sm font-medium';
                          const itemText = itemCount === 1 ? 'Item' : 'Items';
                          newDrawerCount.textContent = `(${itemCount} ${itemText})`;
                          headerTitleContainer.appendChild(newDrawerCount);
                        }
                      })
                  } else if (itemCount > 0) {
                    const newDrawerCount = document.createElement('span');
                    newDrawerCount.className = 'cart-drawer__count text-sm font-medium';
                    const itemText = itemCount === 1 ? 'Item' : 'Items';
                    newDrawerCount.textContent = `(${itemCount} ${itemText})`;
                    headerTitleContainer.appendChild(newDrawerCount);
                  }
                }
              }
            }
          }
        } else {
          _rcLog('DOM-update: element #' + section.id + ' NOT FOUND in DOM');
        }
      });

      _rcLog('DOM-update: all sections written');

      this.cartSummary = this.querySelector('.cart-drawer__summary');

      if (openDrawer && this.getAttribute('open') === null) {
        _rcLog('open: scheduling this.open() via setTimeout');
        setTimeout(() => this.open());
      } else if (openDrawer) {
        _rcLog('open: drawer already open, skipping');
      } else {
        _rcLog('open: openDrawer=false, not opening');
      }

      _rcLog('initLazyImages + recalcCssVarHeights START');
      window.initLazyImages();
      CartDrawer.recalculateCssVarHeights();
      _rcLog('DONE');
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      const sections = [
        {
          id: 'cart-drawer',
          section: this.closest('.shopify-section').id.replace('shopify-section-', ''),
          selector: 'cart-drawer'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];

      // Append any quick order lists to the sections
      document.querySelectorAll('quick-order-list').forEach((quickOrderList) => {
        const closestSection = quickOrderList.closest('.shopify-section');
        if (closestSection) {
          const sectionId = closestSection.id.replace('shopify-section-', '');
          sections.push({
            id: `quick-order-list_${sectionId}`,
            section: sectionId,
            selector: `#quick-order-list_${sectionId}`
          });
        }
      });

      return sections;
    }

    /**
     * Dispatches an event to recalculate the data-css-var-heights on the page. Useful for
     * updating the height of the sticky cart buttons
     */
    static recalculateCssVarHeights() {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('on:css-var-height:update'));
      });
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      return tmpl.content.querySelector(selector).innerHTML;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}